/**
 * CategoryMenu helpers.
 *
 * The menu reads its data via `useConfiguration().categoryConfiguration`,
 * which returns the merged hosted-asset + local-config category tree
 * (see `src/util/configHelpers.js`). These pure helpers shape that tree
 * for the navigation UI: filtering hidden entries, building URL params,
 * and answering branch-vs-leaf questions.
 */

/**
 * Drop categories flagged as `hidden: true`. Applied recursively so a
 * hidden subcategory doesn't appear under a visible parent either.
 *
 * @param {Array} categories category tree from configuration
 * @returns {Array} same tree with hidden nodes removed
 */
export const visibleCategories = (categories = []) => {
  return categories
    .filter(category => !category.hidden)
    .map(category => ({
      ...category,
      subcategories: visibleCategories(category.subcategories),
    }));
};

/**
 * Build the `?pub_categoryLevel1=…&pub_categoryLevel2=…` search string
 * for a category path. Returns an empty string for an empty path so the
 * caller can hand it straight to NamedLink's `to.search` prop.
 *
 * @param {Array<string>} path ids of categories from root downward,
 *                              e.g. ['klar', 'klar-jakker']
 * @returns {string} '?pub_categoryLevel1=klar&pub_categoryLevel2=klar-jakker'
 */
export const categorySearchString = (path = []) => {
  if (path.length === 0) {
    return '';
  }
  const parts = path.map(
    (id, index) => `pub_categoryLevel${index + 1}=${encodeURIComponent(id)}`
  );
  return `?${parts.join('&')}`;
};

/**
 * True if the category has at least one subcategory.
 *
 * @param {Object} category
 * @returns {boolean}
 */
export const hasChildren = category => {
  return Array.isArray(category?.subcategories) && category.subcategories.length > 0;
};
